import React, { useMemo } from "react";
import CardLayout from "components/foundation/templates/CardLayout";
import SelectButton from "components/foundation/SelectButton";
import { CheckIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import styled from "styled-components";
import { useRecoilState, useSetRecoilState } from "recoil";
import { DOCUMENT_UUID, KOREA_REGION } from "config/constant";
import useTagsByDocumentCategoryQuery from "features/tag/hooks/queries/useTagsByDocumentCategoryQuery";
import {
  analysisFormStepState,
  selectedAnalysisBusinessTagState,
  selectedAnalysisCityState,
  selectedAnalysisProvinceState,
} from "store/document/analysisDocument";
import Select from "components/foundation/Select/Select";
import Modal from "components/foundation/Modal/Modal";
import TempDocumentModal from "components/document/Create/TempDocumentModal";
import useTempDocModal from "features/document/hooks/create/useTempDocModal";

const AnalysisCreateSelectPage = () => {
  /* 지역구분 태그 */
  const [selectedProvince, setSelectedProvince] = useRecoilState(
    selectedAnalysisProvinceState,
  );
  const [selectedCity, setSelectedCity] = useRecoilState(
    selectedAnalysisCityState,
  );

  const { provinceOptions, cityOptions } =
    useAnaylsisSelectFormOptions(selectedProvince);

  /* 사업구분 태그 */
  const { data: analysisTags } = useTagsByDocumentCategoryQuery(
    DOCUMENT_UUID.ANALYSIS.uuid,
  );
  const busniessTags = analysisTags?.tagCategories[0].tags;

  const [selectedBusinessTag, setSelectedBusinessTag] = useRecoilState(
    selectedAnalysisBusinessTagState,
  );

  /* 다음 버튼 */
  const nextButtonDisabled = !selectedProvince || !selectedBusinessTag;
  const { onClickNext } = useNextButton();

  /* 임시 저장 문서 모달 */
  const { openTempSaveDocsModal, setOpenTempSaveDocsModal } = useTempDocModal({
    categoryUuid: DOCUMENT_UUID.ANALYSIS.uuid,
  });

  return (
    <>
      <CardLayout.Container
        header={{
          title: "문서 등록",
          description: "문서에 들어갈 기본 태그를 선택해 주세요.",
        }}
        gap={40}
      >
        <DocumentAnalysisCreateForm>
          <CardLayout.Field label="지역선택">
            <RegionSelectContainerStyle>
              <Select
                list={provinceOptions}
                placeholder="시/도"
                value={selectedProvince}
                onValueChange={(value: keyof typeof KOREA_REGION) => {
                  setSelectedProvince(value);

                  if (selectedProvince !== value) {
                    // Radix-UI GitHub : Select를 placeholder로 되돌리기
                    // https://github.com/radix-ui/primitives/issues/1569#issuecomment-1196416512
                    setSelectedCity("");
                  }
                }}
              />
              <Select
                list={cityOptions}
                placeholder="시/군/구"
                value={selectedCity as string | undefined}
                onValueChange={(value) => {
                  if (selectedProvince) {
                    setSelectedCity(value || "");
                  }
                }}
              />
            </RegionSelectContainerStyle>
          </CardLayout.Field>

          <CardLayout.Field label="사업구분">
            <SelectContainerStyle>
              {busniessTags?.map((tag) => {
                const selected = tag.tagUuid === selectedBusinessTag?.tagUuid;

                return (
                  <SelectButton
                    key={tag.tagUuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => setSelectedBusinessTag(tag)}
                  >
                    {tag.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
        </DocumentAnalysisCreateForm>
        <Divider />
        <Button
          variant="primary"
          size="large"
          onClick={onClickNext}
          disabled={nextButtonDisabled}
        >
          다음
        </Button>
      </CardLayout.Container>

      <Modal.Root
        open={openTempSaveDocsModal}
        onOpenChange={setOpenTempSaveDocsModal}
      >
        <TempDocumentModal
          defaultTabIndex={1}
          onCancel={() => setOpenTempSaveDocsModal(false)}
        />
      </Modal.Root>
    </>
  );
};

export default AnalysisCreateSelectPage;

/* Hooks */
const useAnaylsisSelectFormOptions = (
  selectedProvince: keyof typeof KOREA_REGION,
) => {
  const provinceOptions = useMemo(() => {
    return Object.keys(KOREA_REGION).map((region) => ({
      label: region,
      value: region,
    }));
  }, []);

  const cityOptions = useMemo(() => {
    return selectedProvince
      ? KOREA_REGION[selectedProvince].map((city) => ({
          label: city,
          value: city,
        }))
      : [];
  }, [selectedProvince]);

  return {
    provinceOptions,
    cityOptions,
  };
};

const useNextButton = () => {
  const setAnalysisFormStep = useSetRecoilState(analysisFormStepState);

  /**
   * @description 다음 버튼 클릭 이벤트 리스너
   */
  const onClickNext = () => {
    setAnalysisFormStep(1);
  };

  return {
    onClickNext,
  };
};

/* Styles */
const DocumentAnalysisCreateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const RegionSelectContainerStyle = styled.div`
  display: flex;
  gap: 1rem;

  & > * {
    flex: 1;
  }
`;

const SelectContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.2rem, 1fr));
  gap: 1rem;
`;
